import { useLocale, useT } from '../../i18n';
import { LOCALES, type Locale } from '../../i18n';

// ═══════════════════════════════════════════════════════════════════
// CHOOSING THE UI LANGUAGE (#310).
//
// ── A FLAG IS A COUNTRY, NOT A LANGUAGE ────────────────────────────
//
// So the flag never stands alone. #310 settled this: a flag picker is fine
// «provided the selected language is also named in text so it is
// unambiguous». The glyph is recognisable at a glance in a header; the word
// is what makes it correct.
//
// ── THE NAMES ARE ENDONYMS, AND THAT IS DELIBERATE ─────────────────
//
// «Norsk» and «English» — each language written in ITSELF, identical in every
// catalog, never translated. A picker whose options are translated into the
// language you cannot read is the one control in the product that must work
// for somebody who is lost: a Norwegian member who lands in English needs to
// find «Norsk», not «Norwegian». That is why these two keys are in the
// contract test's SAME_ON_PURPOSE list rather than being a translation
// oversight.
// ═══════════════════════════════════════════════════════════════════

/** One glyph per locale. Kept here, beside the reason it is not enough. */
const FLAG: Record<Locale, string> = { nb: '🇳🇴', en: '🇬🇧' };

export interface LanguagePickerProps {
  /** Header/login chrome: smaller type, no visible label of its own. */
  compact?: boolean;
  className?: string;
  /**
   * DOM id, for a surface that labels the control itself.
   *
   * `AccountView` has a visible «Språk» label bound to it — and its tests
   * reach for that id to switch language. Both are the caller's contract, so
   * the id is passed in rather than owned here.
   */
  id?: string;
}

/**
 * WHO STILL SEES A LANGUAGE PICKER WHILE i18n IS PARKED (#533).
 *
 * The translation work is deliberately deprioritised: the product owner is
 * rebuilding ownership of the product itself, and a half-translated app is a
 * distraction from that rather than a feature. So the two FLAG pickers — the
 * header and the login screen — are gone from the UI, and the per-account
 * preference in account settings stays reachable for one test account.
 *
 * NOT DELETED, PARKED. Every catalog key, the `nb`/`en` catalogs, the
 * ratchet and 144 files of recorded debt are untouched, so restoring this is
 * deleting a condition rather than redoing a migration.
 *
 * AN E-MAIL IN THE CLIENT IS NOT AN AUTHORIZATION BOUNDARY, and this is not
 * pretending to be one: choosing your own display language is not a
 * privilege, and nothing behind it is protected. It is a visibility switch
 * with an owner, and it is written here once so there is one place to delete.
 */
const LANGUAGE_PREVIEW_ACCOUNTS = ['jenstf@gmail.com'];

/** Is this account one of the few that still sees the picker? See above. */
export function showsLanguagePicker(email: string | null | undefined): boolean {
  const normalised = (email ?? '').trim().toLowerCase();
  return normalised !== '' && LANGUAGE_PREVIEW_ACCOUNTS.includes(normalised);
}

export function LanguagePicker({ compact = false, className = '', id }: LanguagePickerProps) {
  const { locale, setLocale } = useLocale();
  const t = useT();
  return (
    <select
      id={id}
      // NAMED FOR SCREEN READERS EVEN WHEN COMPACT. The visible text is the
      // language itself, which says what the control holds but not what it
      // does; the label is what says «this chooses a language».
      aria-label={t('account.language')}
      value={locale}
      // `void`, because setting the language is about to become a WRITE —
      // the signed-in preference is stored on the account — and an onChange
      // handler cannot await. The store settles the UI itself; nothing here
      // needs the result.
      onChange={(e) => void setLocale(e.target.value as Locale)}
      className={`${compact ? 'btn-outline text-xs px-2 py-1' : 'input'} ${className}`.trim()}
    >
      {LOCALES.map((l) => (
        <option key={l} value={l}>
          {FLAG[l]} {t(`language.endonym.${l}` as never)}
        </option>
      ))}
    </select>
  );
}
